import { useState, useEffect, ChangeEvent } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "~/redux/rootReducer";
import { initializeSpecialist } from "~/redux/modules/admin/data";
import {
  setLoading,
  setSuccess,
  setError,
  resetAlert,
} from "~/redux/modules/admin/screen";
import { getColumns } from "../const/column";
import { Specialist } from "~/types/specialist";
import { db } from "~/plugins/firebase";
import { specialistsCollection } from "~/const/firestore";

export const useModule = () => {
  const dispatch = useDispatch();

  // 画面の状態を制御
  const startLoading = () => dispatch(setLoading(true));
  const stopLoading = () => dispatch(setLoading(false));
  const resetAlertMessage = () => dispatch(resetAlert());
  const setSuccessMessage = (val: string) => dispatch(setSuccess(val));
  const setErrorMessage = (val: string) => dispatch(setError(val));

  // カラムデータ
  const master = useSelector((state: RootState) => state.master);
  const columns = getColumns(master);

  // ダイアログ
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleOpenDialog = () => setOpenDialog(true);

  // フィルター
  const [filterState, setFilterState] = useState({
    public: true, // 公開
    private: true, // 非公開
  });
  const handleChangeFilterState = (event: ChangeEvent<HTMLInputElement>) => {
    setFilterState({
      ...filterState,
      [event.target.name]: event.target.checked,
    });
  };

  // 専門家データ
  const adminData = useSelector((state: RootState) => state.adminData);
  const isSpecialistInit = useSelector(
    (state: RootState) => state.adminData.isSpecialistInit
  );
  const [specialists, setSpecialist] = useState<Specialist[]>([]);
  useEffect(() => {
    const _public = filterState.public;
    const _private = filterState.private;
    let data = adminData.specialists;
    if (_public && !_private) {
      data = data.filter((item) => item.isPublic === true);
    } else if (!_public && _private) {
      data = data.filter((item) => item.isPublic !== true);
    } else if (!_public && !_private) {
      data = [];
    }
    setSpecialist(data);
  }, [adminData.specialists, filterState]);
  const searchSpecialist = (uid: string) => {
    const result = specialists.filter((item) => item.uid === uid);
    if (result.length > 0) {
      return result[0];
    } else {
      return null;
    }
  };

  // 選択されている専門家
  const [selected, setSelected] = useState<Specialist[]>([]);
  const isSelected = (uid: string) =>
    selected.find((item) => item.uid === uid) !== undefined;
  const handleClick = (uid: string) => {
    let newSelected = selected;
    newSelected = newSelected.filter((item) => item.uid !== uid);
    const _specialist = searchSpecialist(uid);
    if (!isSelected(uid) && _specialist !== null) {
      newSelected.push(_specialist);
    }
    setSelected(newSelected);
  };

  // 専門家情報 公開設定 更新
  const updatePublicInfo = async () => {
    // dialogを閉じて、ロード開始
    startLoading();
    setOpenDialog(false);
    resetAlertMessage();

    try {
      await db.runTransaction((transaction) => {
        // タスクを登録
        const promises: Promise<any>[] = [];
        selected.forEach((specialist) => {
          const uid = specialist.uid;
          const ref = specialistsCollection.doc(uid);
          const t = transaction.get(ref).then((doc) => {
            if (!doc.exists) {
              throw "Error: Document does not exist!";
            }
            const data = doc.data() as Specialist | undefined;
            if (data) {
              transaction.update(ref, { isPublic: !data.isPublic });
            }
          });
          promises.push(t);
        });

        // タスクを実行
        return Promise.all(promises);
      });

      // 初期化処理
      dispatch(initializeSpecialist());
      setSelected([]);
      setTimeout(() => setSuccessMessage("公開設定の更新をしました"), 100);
    } catch (error) {
      console.log(error);
      setErrorMessage("公開設定の更新に失敗しました");
    } finally {
      stopLoading();
    }
  };

  // ページネーション
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return {
    states: {
      isSpecialistInit,
      specialists,
      columns,
      filterState,
      selected,
      openDialog,
      page,
      rowsPerPage,
    },
    setters: {
      handleChangeFilterState,
      handleCloseDialog,
      handleOpenDialog,
      handleChangePage,
      handleChangeRowsPerPage,
    },
    actions: { isSelected, handleClick, updatePublicInfo },
  };
};
